import React, { Component, useState, useEffect, useRef } from 'react';
import { Route, Redirect } from 'react-router-dom';
import {
  isOwner,
  inMode,
  canViewFreeImpactReport,
} from 'javascripts/analytics/config/permissions';
import { AllTimeDateRangePickerContainer } from './components/dates/all_time_date_range_picker_container';
import { LimitedDateRangePickerContainer } from './components/dates/limited_date_range_picker_container';
import { ActivityListLink } from './components/links';
import { ScrollableTabNav } from './components/ScrollableTabNav.wjs';
import { AnalyticsTabRoute } from './components/AnalyticsTabRoute.wjs';
import {
  SurfacesContext,
  useSurfaces,
  rootDataAttrs,
  rootStyleFor,
  defaultTabFor,
} from 'js/analytics/autotest/AutotestAnalytics';
import {
  startTab,
  observeLoading,
} from 'js/analytics/metrics/tabActivityTracking';

const DEFAULT_TAB_ORDER = [
  'overview',
  'reach',
  'profile_visitors',
  'impact',
  'documents',
  'readers',
  'cv',
  'countries',
  'keywords',
  'links',
];

import { ProfileVisitorsContainer } from './containers/profile_visitors_container';
import { ConnectedOverviewTabContainer } from './containers/overview_tab/overview_tab_container';
import { DocumentsTabContainer } from './containers/documents_tab/documents_tab_container';
import { DocumentsShowContainer } from './containers/DocumentsShowContainer';
import { KeywordsTabContainer } from './containers/keywords_tab/keywords_tab_container';
import { LinksTabContainer } from './containers/links_tab/links_tab_container';
import { CountriesTabContainer } from './containers/countries_tab_container';
import { ReadersTabContainer } from './containers/readers_tab/readers_tab_container';
import { SwitchableImpactTabContainer } from './containers/impact_tab/switchable_impact_tab_container';
import { ReachTabContainer } from './containers/reach_tab/reach_tab_container';

import { UnseenProfileVisitorsCountService } from 'javascripts/redux_store/util/fetch_services';
import { MarkProfileVisitorsAsReadService } from 'javascripts/redux_store/util/post_services';

const PATH_PROFILE_VISITORS = '/profile-visitors';

const ResponsiveContainer = ({ children, className = '' }) => {
  const [isWideScreen, setIsWideScreen] = useState(window.innerWidth > 1000);

  useEffect(() => {
    const handleResize = () => {
      setIsWideScreen(window.innerWidth > 1000);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const containerClass = isWideScreen ? 'analytics-dashboard-container' : '';
  const combinedClassName = `${containerClass} ${className}`.trim();
  const isNarrow = !isWideScreen;

  return (
    <div className={combinedClassName}>
      {typeof children === 'function' ? children({ isNarrow }) : children}
    </div>
  );
};

export class ActivityContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      [`redDot_${PATH_PROFILE_VISITORS}`]: false,
    };
  }

  componentDidMount() {
    UnseenProfileVisitorsCountService().done(({ has_unseen }) => {
      if (has_unseen) {
        this.setState(
          { [`redDot_${PATH_PROFILE_VISITORS}`]: true },
          this.onMatchProfileVisitors,
        );
      }
    });
  }

  componentWillReceiveProps(nextProps) {
    this.onMatchProfileVisitors(nextProps);
  }

  onMatchProfileVisitors = (props = this.props, state = this.state) => {
    // currently this is for profile-visitors only because of the Ajax fn
    const checkLocation =
      props.location.pathname === this.relPath(PATH_PROFILE_VISITORS);
    const checkState = state[`redDot_${PATH_PROFILE_VISITORS}`];

    if (checkLocation && checkState) {
      MarkProfileVisitorsAsReadService().done(({ success }) => {
        if (success)
          this.setState({ [`redDot_${PATH_PROFILE_VISITORS}`]: false });
      });
    }
  };

  relPath = (path) => {
    if (path[0] !== '/') {
      return this.props.match.url + '/' + path;
    }
    return this.props.match.url + path;
  };

  absolutePath = (path) => {
    const formattedPath = path.startsWith('/') ? path : `/${path}`;
    const domain = window.location.origin;
    return `${domain}${formattedPath}`;
  };

  render() {
    // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { relPath, absolutePath, props } = this;
    const isPremium = !!inMode('premium');
    const isUpgradeable = !!inMode('upgradeable');

    const hashUrl = this.props.location.hash;
    const hashIdentifier = '#/activity';
    if (hashUrl.includes(hashIdentifier)) {
      // redirect hash urls to actual paths
      return <Redirect to={relPath(hashUrl.slice(hashIdentifier.length))} />;
    }

    // Autotest slots (default = control). Tab links are keyed so a variant can
    // reorder (tabOrder) or relabel (tabLabels) them via config; the JSX for each
    // link is unchanged from control.
    const surfaces = this.context;
    const tabLabel = (key, dflt) =>
      (surfaces.tabLabels && surfaces.tabLabels[key]) || dflt;
    const tabLinks = {
      overview: (
        <ActivityListLink
          key="overview"
          {...{
            to: relPath('/overview'),
            text: tabLabel('overview', 'Overview'),
          }}
        />
      ),
      profile_visitors: (
        <ActivityListLink
          key="profile_visitors"
          {...{
            isPremium,
            hide: !(isUpgradeable || isPremium),
            to: relPath(PATH_PROFILE_VISITORS),
            text: tabLabel('profile_visitors', 'Profile Visitors'),
            premiumFeature: 'analytics',
            upgradeTrigger: 'profile-visitors',
            popoverBodyText: 'Learn more about who visits your profile.',
            popoverButtonText: 'View Your Visitors',
            redDot: this.state[`redDot_${PATH_PROFILE_VISITORS}`],
          }}
        />
      ),
      impact: (
        <ActivityListLink
          key="impact"
          {...{
            isPremium,
            hide: !(isUpgradeable || isPremium || canViewFreeImpactReport),
            to: relPath('/impact'),
            text: tabLabel('impact', 'Impact'),
            premiumFeature:
              !canViewFreeImpactReport && 'academia_analytics_expanded',
            upgradeTrigger: 'analytics-impact',
            popoverBodyText: 'View the impact of your work.',
          }}
        />
      ),
      documents: (
        <ActivityListLink
          key="documents"
          {...{
            to: relPath('/documents'),
            text: tabLabel('documents', 'Papers'),
          }}
        />
      ),
      readers: (
        <ActivityListLink
          key="readers"
          {...{
            isPremium,
            hide: !(isUpgradeable || isPremium),
            to: relPath('/readers'),
            text: tabLabel('readers', 'Readers'),
            premiumFeature: true, // Change from string to boolean
            upgradeTrigger: 'analytics-reader',
            popoverBodyText: 'Learn more about who reads your papers.',
          }}
        />
      ),
      cv: (
        <ActivityListLink
          key="cv"
          {...{
            isPremium,
            hide: !(isUpgradeable || isPremium),
            to: relPath('/documents/' + Aedu.WorkConstants.CV_WORK_ID),
            text: tabLabel('cv', 'CV'),
            premiumFeature: 'academia_analytics_expanded',
            upgradeTrigger: 'analytics-cv',
            popoverBodyText: 'View analytics for your CV.',
          }}
        />
      ),
      countries: (
        <ActivityListLink
          key="countries"
          {...{
            to: relPath('/countries'),
            text: tabLabel('countries', 'Countries'),
          }}
        />
      ),
      keywords: (
        <ActivityListLink
          key="keywords"
          {...{
            hide: inMode('restricted'),
            to: relPath('/keywords'),
            text: tabLabel('keywords', 'Keywords'),
          }}
        />
      ),
      links: (
        <ActivityListLink
          key="links"
          {...{
            hide: inMode('restricted'),
            to: relPath('/links'),
            text: tabLabel('links', 'External Links'),
          }}
        />
      ),
      // Hidden without a snapshot so authors outside the blast get no empty tab;
      // the route below still resolves, so the email CTA works either way.
      reach: (
        <ActivityListLink
          key="reach"
          {...{
            hide: inMode('restricted') || !Aedu.hasReachReport,
            to: relPath('/reach'),
            text: tabLabel('reach', 'Reach'),
          }}
        />
      ),
    };
    const orderedTabKeys =
      surfaces.tabOrder && surfaces.tabOrder.length
        ? surfaces.tabOrder
        : DEFAULT_TAB_ORDER;
    const defaultTabPath = relPath('/' + defaultTabFor(isOwner, surfaces));

    return (
      <div>
        {isOwner ? (
          surfaces.headerCopy && (
            <div className="u-taCenter u-fs36 u-pv10x u-fw300">
              {surfaces.headerCopy}
            </div>
          )
        ) : (
          <div className="u-taCenter u-fs36 u-pv10x u-fw300">
            Analytics for{' '}
            <a href={Aedu.User.viewed.url}>{Aedu.User.viewed.display_name}</a>
          </div>
        )}

        <nav
          className="navbar u-bgColorWhite u-borderTop1 u-borderColorGrayLight"
          style={{ borderRadius: 0 }}
        >
          <ResponsiveContainer className="container">
            {({ isNarrow }) => (
              <>
                <ScrollableTabNav>
                  {orderedTabKeys
                    .map((key) => tabLinks[key])
                    .filter(Boolean)
                    .map((el) =>
                      surfaces.tabLabelClass
                        ? React.cloneElement(el, {
                            className: surfaces.tabLabelClass,
                          })
                        : el,
                    )}
                </ScrollableTabNav>

                <div
                  className="navbar-form u-floatRight u-mr0x u-pr0x u-pv0x u-mb2x u-pt0x"
                  style={isNarrow ? { marginTop: 0 } : undefined}
                >
                  {surfaces.dateRangeHintCopy && (
                    <span className="u-fs11 u-tcGrayDark u-mr3x">
                      {surfaces.dateRangeHintCopy}
                    </span>
                  )}
                  {isPremium && <AllTimeDateRangePickerContainer />}
                  {!isPremium && <LimitedDateRangePickerContainer />}
                  {isOwner && (
                    <a
                      className="btn btn-default u-fw700 u-tcGrayDarkest u-ml3x Button Button--inverseGray Button--sm u-hover-bgColorGray Button--square"
                      href={Aedu.User.current.url + '/analytics/csv'}
                    >
                      <i className="fa fa-download" />
                    </a>
                  )}
                </div>
              </>
            )}
          </ResponsiveContainer>
        </nav>

        <ResponsiveContainer className="container">
          <Route
            path={relPath('/')}
            exact
            component={() => <Redirect to={defaultTabPath} />}
          />
          <AnalyticsTabRoute
            path={relPath('/overview')}
            component={ConnectedOverviewTabContainer}
          />
          <AnalyticsTabRoute
            path={relPath('/readers')}
            component={ReadersTabContainer}
          />
          <AnalyticsTabRoute
            path={relPath(PATH_PROFILE_VISITORS)}
            component={ProfileVisitorsContainer}
          />
          <AnalyticsTabRoute
            path={relPath('/impact')}
            component={SwitchableImpactTabContainer}
            condition={!!(isPremium || canViewFreeImpactReport)}
          />
          <AnalyticsTabRoute
            exact
            path={relPath('/documents')}
            component={DocumentsTabContainer}
          />
          {!!isPremium && (
            <AnalyticsTabRoute
              exact
              path={relPath('/documents/:docId')}
              component={DocumentsShowContainer}
            />
          )}
          <AnalyticsTabRoute
            path={relPath('/countries')}
            component={CountriesTabContainer}
          />
          <AnalyticsTabRoute
            path={relPath('/keywords')}
            component={KeywordsTabContainer}
          />
          <AnalyticsTabRoute
            path={relPath('/links')}
            component={LinksTabContainer}
          />
          <AnalyticsTabRoute
            path={relPath('/reach')}
            component={ReachTabContainer}
          />
        </ResponsiveContainer>
      </div>
    );
  }
}

ActivityContainer.contextType = SurfacesContext;

export const AnalyticsContainer = () => {
  // Root wrapper carries the autotest density/sticky data-attrs + CSS custom
  // properties that the dashboard SCSS reads via var(--x, <control-default>).
  const surfaces = useSurfaces();

  // Per-tab engagement telemetry (isolated per-tab time-on-site, see
  // js/analytics/metrics/tabActivityTracking). Begin tracking the landing
  // tab-hit here, in render, once — before the tab tree mounts — so the first
  // tab's data-fetch completion is captured. Later tab-hits are (re)started in
  // AnalyticsTabRoute after loadRecordHit re-keys the hit.
  const tabTrackingStarted = useRef(false);
  if (!tabTrackingStarted.current) {
    tabTrackingStarted.current = true;
    startTab();
  }

  // Drive the per-tab "content ready" signal uniformly off the Redux store:
  // ready == the AnalyticsDashboard fetch(es) draining to idle (data-fetch
  // complete). One subscription covers every Redux-backed tab; the module's
  // grace-period fallback covers tabs that render without a fetch.
  useEffect(() => {
    const store = window.Aedu && window.Aedu.ReduxStore;
    if (!store) return undefined;
    const report = () => {
      const dash = store.getState().AnalyticsDashboard || {};
      const anyFetching = Object.values(dash).some(
        (slice) => slice && slice.meta && slice.meta.fetching,
      );
      observeLoading(anyFetching);
    };
    report();
    return store.subscribe(report);
  }, []);

  return (
    <div {...rootDataAttrs(surfaces)} style={rootStyleFor(surfaces)}>
      <Route exact path="/" render={() => <Redirect to="/activity" />} />
      <Route path="/activity" component={ActivityContainer} />
    </div>
  );
};
